import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, Handshake, Calculator, FileBox, LogOut, Download, Loader2, Search, FileText, ClipboardList, Library, Package, BarChart3, Brain, Users, Network } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { adminApi, clearToken, getToken } from "../lib/authApi";
import { AdminRfqSection } from "../components/AdminRfqSection";
import { AdminLibrarySection } from "../components/AdminLibrarySection";
import { AdminPartnersSection } from "../components/AdminPartnersSection";
import { AdminAccountsSection } from "../components/accounts/AdminAccountsSection";
import { AdminOrdersSection } from "../components/orders/AdminOrdersSection";
import { AdminAnalytics } from "../components/analytics/AdminAnalytics";
import { BrainInsights } from "../components/brain/BrainInsights";
import { AgentControlCenter } from "../components/agents/AgentControlCenter";

const STATUSES = ["New", "Replied", "In Progress", "Closed"];
const STATUS_STYLES = {
  New: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30",
  Replied: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  "In Progress": "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/30",
  Closed: "bg-slate-500/10 text-slate-500 dark:text-slate-400 border-slate-500/30",
};

const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
const fmtINR = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const StatusControl = ({ item, kind, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const change = async (status) => {
    if (status === item.status) return;
    setSaving(true);
    try {
      await adminApi.patch(`/admin/enquiries/${kind}/${item.id}/status`, { status });
      onChanged(item.id, status);
      toast.success(`Status updated to ${status}`);
    } catch {
      toast.error("Failed to update status.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold ${STATUS_STYLES[item.status] || STATUS_STYLES.New}`} data-testid={`status-badge-${item.id}`}>
        {item.status || "New"}
      </span>
      <Select value={item.status || "New"} onValueChange={change} disabled={saving}>
        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`status-select-${item.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUSES.map((s) => (
            <SelectItem key={s} value={s} data-testid={`status-option-${item.id}-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const FileChips = ({ files, onDownload, downloading }) => {
  if (!files?.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {files.map((f) => (
        <button
          key={f.id}
          onClick={() => onDownload(f)}
          disabled={downloading === f.id}
          data-testid={`admin-file-download-${f.id}`}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary hover:bg-primary/20 transition-colors duration-200"
        >
          {downloading === f.id ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : <Download className="h-3 w-3" strokeWidth={1.5} />}
          {f.name}
        </button>
      ))}
    </div>
  );
};

const Card = ({ children, testId }) => (
  <div className="p-6 rounded-xl bg-card border border-border" data-testid={testId}>{children}</div>
);

const Meta = ({ children }) => <span className="text-xs text-muted-foreground">{children}</span>;

const searchable = (item, fields) => fields.map((f) => item[f] || "").join(" ").toLowerCase();

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [contact, setContact] = useState([]);
  const [partner, setPartner] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [checking, setChecking] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("newest");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const refreshStats = async () => {
    const { data } = await adminApi.get("/admin/stats");
    setStats(data);
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        await adminApi.get("/auth/me");
        const [s, c, p, q, a, r, o] = await Promise.all([
          adminApi.get("/admin/stats"),
          adminApi.get("/admin/enquiries/contact"),
          adminApi.get("/admin/enquiries/partner"),
          adminApi.get("/admin/quotes"),
          adminApi.get("/admin/audit-logs"),
          adminApi.get("/admin/rfqs"),
          adminApi.get("/admin/orders"),
        ]);
        setStats(s.data);
        setContact(c.data);
        setPartner(p.data);
        setQuotes(q.data);
        setAuditLogs(a.data);
        setRfqs(r.data);
        setOrders(o.data);
        setChecking(false);
      } catch {
        clearToken();
        navigate("/admin/login");
      }
    })();
  }, [navigate]);

  const applyView = (list, fields) => {
    let out = list;
    if (statusFilter !== "All") out = out.filter((i) => (i.status || "New") === statusFilter);
    const q = query.trim().toLowerCase();
    if (q) out = out.filter((i) => searchable(i, fields).includes(q));
    out = [...out].sort((a, b) =>
      sortOrder === "newest"
        ? new Date(b.created_at) - new Date(a.created_at)
        : new Date(a.created_at) - new Date(b.created_at)
    );
    return out;
  };

  const contactView = useMemo(
    () => applyView(contact, ["name", "email", "company", "category", "message"]),
    [contact, query, statusFilter, sortOrder] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const partnerView = useMemo(
    () => applyView(partner, ["company_name", "contact_name", "email", "phone", "partner_type", "message"]),
    [partner, query, statusFilter, sortOrder] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const quotesView = useMemo(
    () => applyView(quotes, ["name", "email", "company", "process", "material", "notes"]),
    [quotes, query, statusFilter, sortOrder] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setStatusLocal = (setter) => (id, status) => {
    setter((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    refreshStats().catch(() => {});
  };

  const download = async (f) => {
    setDownloading(f.id);
    try {
      const res = await adminApi.get(`/admin/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed.");
    } finally {
      setDownloading(null);
    }
  };

  const downloadQuotePdf = async (q) => {
    setPdfDownloading(q.id);
    try {
      const res = await adminApi.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vectron-Quote-VG-${q.id.slice(0, 8).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed.");
    } finally {
      setPdfDownloading(null);
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  if (checking) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" strokeWidth={1.5} />
      </div>
    );
  }

  const STAT_CARDS = [
    { icon: Inbox, label: "Contact Enquiries", value: stats.contact_enquiries },
    { icon: Handshake, label: "Partner Enquiries", value: stats.partner_enquiries },
    { icon: Calculator, label: "Quote Requests", value: stats.quote_requests },
    { icon: ClipboardList, label: "RFQs", value: stats.rfqs ?? 0 },
    { icon: Package, label: "Orders", value: stats.orders ?? 0 },
    { icon: FileBox, label: "Uploaded Files", value: stats.files },
  ];

  return (
    <div className="py-16 sm:py-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary font-mono-tech mb-2">Admin</p>
            <h1 className="text-3xl sm:text-4xl font-medium tracking-tight font-heading">Enquiry Dashboard</h1>
          </div>
          <Button variant="outline" onClick={logout} className="rounded-full" data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Logout
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {STAT_CARDS.map((s) => (
            <div key={s.label} className="p-6 rounded-xl bg-card border border-border" data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <s.icon className="h-5 w-5 text-primary mb-4" strokeWidth={1.5} />
              <p className="font-mono-tech text-3xl font-bold">{s.value}</p>
              <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {STATUSES.map((s) => (
            <div key={s} className={`px-5 py-4 rounded-xl border flex items-center justify-between ${STATUS_STYLES[s]}`} data-testid={`admin-status-count-${s.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="text-sm font-semibold">{s}</span>
              <span className="font-mono-tech text-xl font-bold">{stats.status_counts?.[s] ?? 0}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="admin-toolbar">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, company..." className="pl-9" data-testid="admin-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="admin-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All" data-testid="admin-filter-option-all">All Statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`admin-filter-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortOrder} onValueChange={setSortOrder}>
            <SelectTrigger className="w-[140px]" data-testid="admin-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest" data-testid="admin-sort-newest">Newest First</SelectItem>
              <SelectItem value="oldest" data-testid="admin-sort-oldest">Oldest First</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Tabs value={searchParams.get("tab") || "contact"} onValueChange={(v) => setSearchParams({ tab: v })}>
          <TabsList className="w-full sm:w-auto justify-start overflow-x-auto scrollbar-none tab-strip-fade h-auto" data-testid="admin-tabs">
            <TabsTrigger value="contact" data-testid="admin-tab-contact">Contact ({contactView.length})</TabsTrigger>
            <TabsTrigger value="partner" data-testid="admin-tab-partner">Partners ({partnerView.length})</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Quotes ({quotesView.length})</TabsTrigger>
            <TabsTrigger value="rfqs" data-testid="admin-tab-rfqs"><ClipboardList className="h-4 w-4 mr-1.5" strokeWidth={1.5} />RFQs ({rfqs.length})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders"><Package className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Orders ({orders.length})</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics"><BarChart3 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Analytics</TabsTrigger>
            <TabsTrigger value="brain" data-testid="admin-tab-brain"><Brain className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Brain</TabsTrigger>
            <TabsTrigger value="agents" data-testid="admin-tab-agents"><Network className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Agent Control Center</TabsTrigger>
            <TabsTrigger value="library" data-testid="admin-tab-library"><Library className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Library</TabsTrigger>
            <TabsTrigger value="partner-accounts" data-testid="admin-tab-partner-accounts"><Handshake className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Partner Accounts</TabsTrigger>
            <TabsTrigger value="accounts" data-testid="admin-tab-accounts"><Users className="h-4 w-4 mr-1.5" strokeWidth={1.5} />User &amp; Account Management</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit ({auditLogs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="accounts" className="mt-6">
            <AdminAccountsSection />
          </TabsContent>

          <TabsContent value="partner-accounts" className="mt-6">
            <AdminPartnersSection />
          </TabsContent>

          <TabsContent value="rfqs" className="mt-6">
            <AdminRfqSection rfqs={rfqs} setRfqs={setRfqs} onDownloadFile={download} downloading={downloading} />
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <AdminOrdersSection orders={orders} setOrders={setOrders} onDownloadFile={download} downloading={downloading} />
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <AdminAnalytics />
          </TabsContent>

          <TabsContent value="brain" className="mt-6">
            <BrainInsights />
          </TabsContent>

          <TabsContent value="agents" className="mt-6">
            <AgentControlCenter />
          </TabsContent>

          <TabsContent value="library" className="mt-6">
            <AdminLibrarySection />
          </TabsContent>

          <TabsContent value="contact" className="mt-6 space-y-4">
            {contactView.length === 0 && <p className="text-sm text-muted-foreground py-8" data-testid="admin-contact-empty">No contact enquiries match.</p>}
            {contactView.map((e) => (
              <Card key={e.id} testId={`admin-contact-${e.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <h4 className="font-semibold font-heading">{e.name || "—"}</h4>
                    {e.category && <Badge variant="secondary">{e.category}</Badge>}
                  </div>
                  <div className="flex items-center gap-4">
                    <StatusControl item={e} kind="contact" onChanged={setStatusLocal(setContact)} />
                    <Meta>{fmtDate(e.created_at)}</Meta>
                  </div>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{e.email}{e.company ? ` · ${e.company}` : ""}</p>
                <p className="mt-3 text-sm leading-relaxed">{e.message}</p>
                <FileChips files={e.files} onDownload={download} downloading={downloading} />
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="partner" className="mt-6 space-y-4">
            {partnerView.length === 0 && <p className="text-sm text-muted-foreground py-8" data-testid="admin-partner-empty">No partner enquiries match.</p>}
            {partnerView.map((e) => (
              <Card key={e.id} testId={`admin-partner-${e.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <h4 className="font-semibold font-heading">{e.company_name}</h4>
                    <Badge variant="secondary">{e.partner_type}</Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <StatusControl item={e} kind="partner" onChanged={setStatusLocal(setPartner)} />
                    <Meta>{fmtDate(e.created_at)}</Meta>
                  </div>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{e.contact_name} · {e.email}{e.phone ? ` · ${e.phone}` : ""}</p>
                {e.message && <p className="mt-3 text-sm leading-relaxed">{e.message}</p>}
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="quotes" className="mt-6 space-y-4">
            {quotesView.length === 0 && <p className="text-sm text-muted-foreground py-8" data-testid="admin-quotes-empty">No quote requests match.</p>}
            {quotesView.map((e) => (
              <Card key={e.id} testId={`admin-quote-${e.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <h4 className="font-semibold font-heading">{e.name}</h4>
                    <Badge variant="secondary">{e.process}</Badge>
                    <Badge variant="outline">{e.material} × {e.quantity}</Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <StatusControl item={e} kind="quote" onChanged={setStatusLocal(setQuotes)} />
                    <Meta>{fmtDate(e.created_at)}</Meta>
                  </div>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{e.email}{e.company ? ` · ${e.company}` : ""}</p>
                <div className="mt-3 flex flex-wrap items-center gap-4">
                  <p className="font-mono-tech text-sm font-bold text-primary">
                    Indicative: {fmtINR(e.estimate_min)} – {fmtINR(e.estimate_max)}
                  </p>
                  <button
                    onClick={() => downloadQuotePdf(e)}
                    disabled={pdfDownloading === e.id}
                    data-testid={`admin-quote-pdf-${e.id}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/30 text-xs font-semibold text-accent hover:bg-accent/20 transition-colors duration-200"
                  >
                    {pdfDownloading === e.id ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : <FileText className="h-3 w-3" strokeWidth={1.5} />}
                    Download PDF
                  </button>
                </div>
                {e.notes && <p className="mt-2 text-sm leading-relaxed">{e.notes}</p>}
                <FileChips files={e.files} onDownload={download} downloading={downloading} />
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="audit" className="mt-6 space-y-2">
            {auditLogs.length === 0 && <p className="text-sm text-muted-foreground py-8" data-testid="admin-audit-empty">No audit events yet.</p>}
            {auditLogs.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-lg bg-card border border-border text-sm" data-testid={`admin-audit-${l.id}`}>
                <Badge variant="secondary" className="font-mono-tech text-xs">{l.action}</Badge>
                <span className="text-muted-foreground">{l.actor_email || "—"}</span>
                {l.actor_role && <Badge variant="outline" className="text-xs">{l.actor_role}</Badge>}
                {l.entity_type && <span className="text-xs text-muted-foreground">{l.entity_type}:{(l.entity_id || "").slice(0, 8)}</span>}
                {l.meta?.status && <Badge variant="outline" className="text-xs">→ {l.meta.status}</Badge>}
                <span className="ml-auto text-xs text-muted-foreground">{fmtDate(l.ts)}</span>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
